import { HyperInstantConfig, NormalizedConfig, Strategy } from "./types.js";
import { normalizeUrl } from "./url.js";
import { shouldHandleAnchor, shouldAllowUrl } from "./filters.js";
import { injectSpeculationRules } from "./speculation.js";
import { linkPrefetch } from "./prefetch.js";

declare global {
  interface Window {
    HyperInstant?: HyperInstantConfig;
    HyperInstantStart?: () => void;
  }
}

function normalizeConfig(input?: HyperInstantConfig): NormalizedConfig {
  const cfg = input || {};
  return {
    strategy: cfg.strategy ?? "auto",
    hoverDelay: cfg.hoverDelay ?? 65,
    touchEnabled: cfg.touchEnabled ?? true,

    allowSameOriginOnly: cfg.allowSameOriginOnly ?? true,
    deny: cfg.deny ?? ["/cart", "/checkout", "/my-account", "/account", "/logout", "add-to-cart"],
    allow: cfg.allow ?? [],
    ignoreQueryParams: cfg.ignoreQueryParams ?? true,

    maxPrefetch: cfg.maxPrefetch ?? 6,
    maxPrerender: cfg.maxPrerender ?? 2,

    respectSaveData: cfg.respectSaveData ?? true,
    minEffectiveType: cfg.minEffectiveType ?? "4g",

    debug: cfg.debug ?? false
  };
}

function networkOk(cfg: NormalizedConfig): boolean {
  const nav: any = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

  if (cfg.respectSaveData && (conn?.saveData === true)) return false;

  const eff = conn?.effectiveType as string | undefined;
  if (!eff) return true;

  const order = ["2g", "3g", "4g"];
  const minIdx = order.indexOf(cfg.minEffectiveType);
  const effIdx = order.indexOf(eff);
  if (minIdx === -1 || effIdx === -1) return true;
  return effIdx >= minIdx;
}

export function start(userCfg?: HyperInstantConfig): void {
  const cfg = normalizeConfig(userCfg ?? window.HyperInstant);
  const origin = location.origin;

  if (!networkOk(cfg)) {
    if (cfg.debug) console.log("[HyperInstant] disabled by network/save-data");
    return;
  }

  const seen = new Set<string>();
  let prefetchCount = 0;
  let prerenderCount = 0;

  const hoverTimers = new WeakMap<Element, number>();

  function pickAction(strategy: Strategy): "prefetch" | "prerender" {
    if (strategy === "prefetch-only") return "prefetch";
    if (strategy === "prerender-first") return "prerender";
    if (prerenderCount < cfg.maxPrerender) return "prerender";
    return "prefetch";
  }

  function canProceed(action: "prefetch" | "prerender"): boolean {
    if (action === "prefetch") return prefetchCount < cfg.maxPrefetch;
    return prerenderCount < cfg.maxPrerender;
  }

  function doWarm(urlString: string): void {
    if (seen.has(urlString)) return;
    seen.add(urlString);

    const action = pickAction(cfg.strategy);
    if (!canProceed(action)) return;

    const okSpec = injectSpeculationRules(action, [urlString], cfg.debug);
    if (!okSpec && action === "prefetch") linkPrefetch(urlString, cfg.debug);

    if (action === "prefetch") prefetchCount++;
    else prerenderCount++;
  }

  function handlePotentialLink(a: HTMLAnchorElement): void {
    if (!shouldHandleAnchor(a)) return;

    let url: URL;
    try { url = new URL(a.href, location.href); }
    catch { return; }

    if (!shouldAllowUrl({ url, origin, allowSameOriginOnly: cfg.allowSameOriginOnly, allow: cfg.allow, deny: cfg.deny })) return;

    const norm = normalizeUrl(url, cfg.ignoreQueryParams);
    doWarm(norm);
  }

  document.addEventListener("mouseover", (e) => {
    const t = e.target as Element | null;
    if (!t) return;
    const a = (t.closest?.("a") as HTMLAnchorElement | null);
    if (!a) return;

    const existing = hoverTimers.get(a);
    if (existing) return;

    const id = window.setTimeout(() => {
      hoverTimers.delete(a);
      handlePotentialLink(a);
    }, cfg.hoverDelay);

    hoverTimers.set(a, id);
  }, { passive: true });

  document.addEventListener("mouseout", (e) => {
    const t = e.target as Element | null;
    if (!t) return;
    const a = (t.closest?.("a") as HTMLAnchorElement | null);
    if (!a) return;

    const id = hoverTimers.get(a);
    if (id) {
      window.clearTimeout(id);
      hoverTimers.delete(a);
    }
  }, { passive: true });

  if (cfg.touchEnabled) {
    document.addEventListener("touchstart", (e) => {
      const t = e.target as Element | null;
      if (!t) return;
      const a = (t.closest?.("a") as HTMLAnchorElement | null);
      if (!a) return;
      handlePotentialLink(a);
    }, { passive: true });
  }

  if (cfg.debug) console.log("[HyperInstant] started", cfg);
}
